import { Component, OnInit, OnDestroy } from '@angular/core';
import { ProjectElement } from '../project-element.interface';
import { Subject } from 'rxjs';
import { ElementSidebarService } from './element-sidebar.service';
import { ElementService } from '../element.service';
import { takeUntil } from 'rxjs/operators';
import { ProjectElementSidebarState, TemplateFormControls } from './element.sidebar.interface';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { LogService } from '../../log/log.service';
import { ElementComment, ElementChange } from '../../log/logs.interface';
import { RouterService } from '../../../utilities/router/router.service';

/**
 * Element sidebar component.
 */
@Component({
  selector: 'sg2-element-sidebar',
  templateUrl: './element-sidebar.component.html',
  styleUrls: ['./element-sidebar.component.less']
})
export class ElementSidebarComponent implements OnDestroy, OnInit {

  /** Unsubscribe hook. */
  private _destroy$: Subject<any> = new Subject;

  /** Current state of the sidebar. */
  public state: ProjectElementSidebarState = null;

  /** Current active element. */
  public element: Partial<ProjectElement>;

  /** Optional formControls for item template. */
  public formControls: TemplateFormControls<ProjectElement>;

  /** Element logs. @todo Cleanup. */
  public logs: ProjectElement['logs'];

  /** Element changes. */
  public changes: ElementChange[];

  /** Element comments. */
  public comments: ElementComment[];

  /** Current visibility, not equal to this.active. */
  public visible = false;

  constructor(
    private elementService: ElementService,
    public elementSidebarService: ElementSidebarService,
    private logService: LogService,
    private routerService: RouterService
  ) { }

  /**
   * @note Also updates when 'dismissed'.
   */
  ngOnInit() {
    this._trackElement();
    this._trackSidebar();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Dismiss sidebar.
   *
   * Performs a garbage collect on the sidebar data.
   *
   * - Set visibility to hidden.
   * - Clear current element.
   * - Collapse the sidebar.
   *
   * @param [onlySelf=false] When set, the event does not propagate to the service, which can
   *  cause loops when this method is called *from* the service.
   */
  public dismiss(onlySelf: boolean = false): void {
    this.visible = false;
    this.element = null;
    if (!onlySelf) {
      this.elementSidebarService.dismiss();
    }
  }

  /**
   * On changing active tab.
   *
   * @param $event The event emitted when switching active tab.
   */
  public onTabChange($event: MatTabChangeEvent): void {
    this.elementSidebarService.onTabChange($event.index);
  }

  /**
   * Get element changes from API.
   */
  private _getChanges(): void {
    this.logService.getElementChanges(this.element as ProjectElement).subscribe(changes => {
      this.changes = changes;
    });
  }

  /**
   * Get element comments from API.
   */
  private _getComments(): void {
    if (this.element) {
      this.logService.getElementComments(this.element as ProjectElement).subscribe(comments => {
        this.comments = comments;
      });
    }
  }

  /**
   * Set visibility to true, some time after `this.active`, to
   * be able to add an animation on init, and have some to load
   * the data.
   */
  private _setViewVisibility(): void {
    if (this.state.open) {
      setTimeout(() => {
        this.visible = true;
      }, 400);
    }
  }

  /**
   * Subscribe to element.
   */
  private _trackElement() {
    this.elementService.element$.pipe(
      takeUntil(this._destroy$)
    ).subscribe(elements => {
      if (this.element) {
        this.element = this.elementService.getElement(this.element._id, { constructionId: this.element.constructionId });
        this._getComments();
      }
    });
  }

  /**
   * Subscribe to log state.
   */
  private _trackSidebar(): void {
    this.elementSidebarService.active$.pipe(
      takeUntil(this._destroy$)
    ).subscribe(state => {
      this.state = state;
      this._setViewVisibility();
      if (this.state.open) {
        this.element = this.state.item;
        this.formControls = this.state.formControls;
        switch (this.state.tab) {
          case 1:
            this._getComments();
            return;
          case 2:
            this._getChanges();
            return;
        }
      } else {
        this.dismiss(true);
      }
    });
  }
}
